import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { isUnreachable } from '../../utils/collectionRecord';
import { leaseActive } from '../../utils/fieldBatches';
import { normaliseTag } from '../../utils/specimenCode';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { coordText, formatMoment, isToday, momentMs, pageStyles as ps } from './fieldFormat';
import { mayAskForHandoff, myHandoffs, statusTone } from '../../utils/handoffs';

/** One page of the server's collections. */
const PAGE = 50;

/**
 * How a batch on this device reads on its card, by what it is really waiting
 * for: a place, a sign-in, a connection, or only its turn.
 */
function localStatus(batch, outboxPhase, now = Date.now()) {
  // Its record is no longer on the server (FieldSentEditor found it so).
  if (batch.gone) return 'gone';
  if (batch.status === 'sent') return 'saved';
  if (batch.status === 'attention') return 'attention';
  if (batch.status === 'sending' && leaseActive(batch, now)) return 'sending';
  if (!batch.location && batch.source === 'camera') return 'locating';
  if (outboxPhase === 'signedOut') return 'signIn';
  if (outboxPhase === 'viewingAs') return 'viewingAs';
  if (outboxPhase === 'offline' || (typeof navigator !== 'undefined' && navigator.onLine === false)) return 'waiting';
  return 'queued';
}

/**
 * The collector's specimens: those still on this phone, and their own
 * collections on the server (GET /collections?mine=1), as one list, newest
 * first. A batch the server already has appears once, as the device's card,
 * which knows its photos and how its sending went.
 *
 * Every card opens the editor. "Add retrospective collection" records a
 * specimen found earlier, from photos already on the phone. Both are pushed
 * with state fromGallery, so the editor's Back is Back itself: to the gallery
 * page, or to the camera with its gallery sheet open (2026-10-21).
 *
 * The list alone, without a frame: the gallery page (FieldGallery.js) puts it
 * under its header, the camera's gallery sheet (FieldCamera.js) in a Sheet.
 * `headingAs` is the level of its Today and Earlier headings, one under the
 * host's own name.
 */
export default function FieldGalleryList({ store, outbox, userId, user = null, headingAs: Heading = 'h2' }) {
  const { t } = useTranslation(['field', 'handoffs']);
  const navigate = useNavigate();
  const [local, setLocal] = useState(null);
  // Each record's handoff to the lab (B6), by sample_id: an invitation, a
  // request and where it stands. Not asked for, or refused: no chips.
  const [handoffs, setHandoffs] = useState({});
  const handoffsShown = !user || mayAskForHandoff(user);
  const [server, setServer] = useState({ state: 'loading', rows: [], more: false });
  const [loadingMore, setLoadingMore] = useState(false);
  const [outboxState, setOutboxState] = useState(outbox.getState());
  const [durable, setDurable] = useState(store.durable ?? null);

  useEffect(() => {
    let alive = true;
    const load = () => store.list(userId)
      .then((all) => { if (alive) setLocal(all.filter(b => b.status !== 'active')); })
      .catch(() => { if (alive) setLocal([]); });
    // The server has had these in full for a fortnight: the device can let go.
    store.prune(userId).catch(() => {}).finally(load);
    const off = store.subscribe(() => {
      load();
      if (alive) setDurable(store.durable ?? null);
    });
    return () => { alive = false; off(); };
  }, [store, userId]);

  useEffect(() => outbox.subscribe(setOutboxState), [outbox]);

  useEffect(() => {
    let alive = true;
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    // An expired token is a 401, and a 401 signs the person out of the app.
    if (!outbox.signedIn()) {
      setServer({ state: 'signedOut', rows: [] });
      return undefined;
    }
    if (offline) {
      setServer({ state: 'offline', rows: [] });
      return undefined;
    }
    api.get('/collections', { params: { mine: 1, limit: PAGE } })
      .then((r) => {
        if (!alive) return;
        const rows = Array.isArray(r.data) ? r.data : [];
        setServer({ state: 'ok', rows, more: rows.length === PAGE });
      })
      .catch((e) => { if (alive) setServer({ state: isUnreachable(e) ? 'offline' : 'failed', rows: [], more: false }); });
    if (handoffsShown) {
      myHandoffs().then((mine) => { if (alive) setHandoffs(mine || {}); }).catch(() => {});
    }
    return () => { alive = false; };
  }, [outbox, handoffsShown]);

  // A request the outbox has sent since (a batch's handoffSent turned true):
  // the server is asked again, once, so its chip stands where "goes with the
  // record" stood. Nothing is asked for the batches as first read, nor for
  // any other change to them.
  const loaded = local !== null;
  const sentHandoffs = (local || []).filter(b => b.handoff && b.handoffSent).map(b => b.id).sort().join(',');
  const seenSent = useRef(null);
  useEffect(() => {
    if (!loaded) return undefined;
    const before = seenSent.current;
    seenSent.current = sentHandoffs;
    if (before === null || !handoffsShown || !outbox.signedIn()) return undefined;
    const had = new Set(before.split(','));
    if (!sentHandoffs.split(',').some(id => id && !had.has(id))) return undefined;
    let alive = true;
    myHandoffs().then((mine) => { if (alive) setHandoffs(mine || {}); }).catch(() => {});
    return () => { alive = false; };
  }, [loaded, sentHandoffs, handoffsShown, outbox]);

  // The next page, after the ones shown: a season's collections are more
  // than one page, and an older one is still the collector's to correct.
  const showEarlier = () => {
    if (loadingMore || !outbox.signedIn()) return;
    setLoadingMore(true);
    api.get('/collections', { params: { mine: 1, limit: PAGE, offset: server.rows.length } })
      .then((r) => {
        const page = Array.isArray(r.data) ? r.data : [];
        setServer((now) => {
          const seen = new Set(now.rows.map(row => String(row.sample_id)));
          return { ...now, rows: [...now.rows, ...page.filter(row => !seen.has(String(row.sample_id)))],
                   more: page.length === PAGE };
        });
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const cards = useMemo(() => {
    const rows = new Map(server.rows.map(r => [String(r.sample_id), r]));
    const out = [];
    for (const batch of local || []) {
      const row = batch.serverSampleId ? rows.get(String(batch.serverSampleId)) : null;
      if (row) rows.delete(String(batch.serverSampleId));
      out.push({ key: `b-${batch.id}`, batch, row, moment: batch.startedAt || batch.createdAt });
    }
    for (const row of rows.values()) out.push({ key: `s-${row.sample_id}`, row, moment: row.collection_datetime });
    return out.sort((a, b) => (momentMs(b.moment) || 0) - (momentMs(a.moment) || 0));
  }, [local, server.rows]);

  const today = cards.filter(c => isToday(c.moment));
  const earlier = cards.filter(c => !isToday(c.moment));
  const waiting = (local || []).filter(b => b.status === 'pending' || b.status === 'sending').length;

  // Marked as opened from here, so the editor's way back is Back itself.
  const open = card => navigate(card.batch ? `/field/batch/${card.batch.id}` : `/field/collection/${card.row.sample_id}`,
    { state: { fromGallery: true } });

  let sending = null;
  if (outboxState.phase === 'sending') sending = t('gallery.sendingNow');
  else if (outboxState.phase === 'elsewhere') sending = t('gallery.sendingElsewhere');
  else if (waiting && outboxState.phase === 'offline') sending = t('gallery.waitingOffline', { count: waiting });
  else if (waiting && outboxState.phase === 'signedOut') sending = t('gallery.waitingSignedOut', { count: waiting });
  else if (waiting && outboxState.phase === 'viewingAs') sending = t('gallery.waitingViewingAs', { count: waiting });
  const unsent = (local || []).some(b => b.status !== 'sent');

  return (
    <>
      <button type="button" style={{ ...ps.primary, width: '100%' }} title={t('tip.gallery.retro')}
        data-testid="gallery-add-retro" onClick={() => navigate('/field/retro', { state: { fromGallery: true } })}>
        {t('gallery.addRetro')}
      </button>

      <p style={ps.hint} role="status" data-testid="gallery-status">{sending}</p>
      {/* The browser may clear these photos if the phone runs short of space
          (it would not promise to keep them: storage.persist). */}
      {durable === false && unsent && (
        <p style={ps.hint} data-testid="gallery-not-durable">{t('gallery.notDurable')}</p>
      )}
      {server.state === 'offline' && <p style={ps.hint} data-testid="gallery-server-offline">{t('gallery.serverOffline')}</p>}
      {server.state === 'signedOut' && <p style={ps.hint}>{t('gallery.serverSignedOut')}</p>}
      {server.state === 'failed' && <p style={ps.hint}>{t('gallery.serverFailed')}</p>}

      {cards.length === 0 && local !== null && server.state !== 'loading' && (
        <p style={ps.hint} data-testid="gallery-empty">{t('gallery.empty')}</p>
      )}

      {[['today', today], ['earlier', earlier]].map(([group, list]) => (list.length > 0 && (
        <section key={group} aria-labelledby={`gallery-${group}`} data-testid={`gallery-group-${group}`}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Heading id={`gallery-${group}`} style={ps.h2}>{t(`gallery.${group}`)}</Heading>
          <ul style={gs.list}>
            {list.map(card => (
              <li key={card.key}>
                <GalleryCard card={card} t={t} phase={outboxState.phase} onOpen={() => open(card)}
                  handoff={handoffs[String(card.row?.sample_id || card.batch?.serverSampleId || '')] || null} />
              </li>
            ))}
          </ul>
        </section>
      )))}
      {server.state === 'ok' && server.more && (
        <button type="button" style={{ ...ps.secondary, alignSelf: 'center' }} title={t('tip.gallery.showEarlier')}
          data-testid="gallery-show-earlier" disabled={loadingMore} onClick={showEarlier}>
          {loadingMore ? t('editor.loading') : t('gallery.showEarlier')}
        </button>
      )}
    </>
  );
}

function GalleryCard({ card, t, phase, onOpen, handoff = null }) {
  const { batch, row } = card;
  let status;
  let code;
  let place;
  let count;
  let thumbnail;
  if (batch) {
    status = localStatus(batch, phase);
    code = batch.serverCode || batch.specimenCode || normaliseTag(batch.details?.label) || row?.code
      || (batch.status === 'sent' ? t('gallery.noCode') : t('gallery.codePending'));
    place = batch.region || row?.region || coordText(batch.location?.lat, batch.location?.lng) || t('gallery.noPlace');
    count = (batch.photos || []).filter(p => !p.skipped).length;
    thumbnail = batch.thumbnail || (row?.thumbnail_url ? resolvePhotoUrl(row.thumbnail_url) : null);
  } else {
    status = row.is_draft ? 'draft' : 'saved';
    // The code the map shows: a specimen's linked later (an observation given
    // one on the map) as well as the record's own QR.
    code = row.code || row.qr_uid || t('gallery.noCode');
    place = row.region || row.locality_text || t('gallery.noPlace');
    count = Number.isFinite(row.photo_count) ? row.photo_count : null;
    // Through resolvePhotoUrl: on the desktop build a stored photo URL names
    // the laptop as 127.0.0.1, which a phone on the LAN cannot load.
    thumbnail = row.thumbnail_url ? resolvePhotoUrl(row.thumbnail_url) : null;
  }
  const time = formatMoment(card.moment);
  const statusText = t(`status.${status}`);
  // Its handoff to the lab: the server's, else one asked for here and not yet sent.
  const queuedHandoff = !handoff && batch?.handoff && !batch.handoffSent;
  const handoffText = handoff
    ? t('handoffs:gallery.chip', { status: t(`handoffs:status.${handoff.status}`) })
    : (queuedHandoff ? t('handoffs:gallery.local') : '');
  const tone = handoff ? statusTone(handoff.status) : statusTone('requested');
  const label = t('gallery.cardLabel', { code, time, place, status: statusText });
  return (
    <button type="button" style={gs.card} onClick={onOpen} data-testid="gallery-card" data-status={status}
      data-kind={batch ? 'device' : 'server'} data-handoff={handoff?.status || (queuedHandoff ? 'queued' : undefined)}
      title={t('tip.gallery.card')}
      aria-label={handoffText ? `${label}, ${handoffText}` : label}>
      <span style={gs.thumb} aria-hidden="true">
        {/* Lazily: a server card's picture is the photo as uploaded, and a
            page of them must not all be fetched before the list is usable. */}
        {thumbnail
          ? <img src={thumbnail} alt="" width={60} height={60} loading="lazy" decoding="async" style={gs.thumbImg} />
          : <span style={gs.thumbIcon}>📷</span>}
      </span>
      <span style={gs.text}>
        <span style={{ ...gs.code, ...ps.mono }} dir="ltr">{code}</span>
        <span style={gs.meta}>{time}{place ? ` · ${place}` : ''}</span>
        <span style={gs.meta}>{count === null ? '' : t('gallery.photos', { count })}</span>
        {handoffText && (
          <span style={{ ...gs.handoff, background: tone.bg, color: tone.fg }} data-testid="gallery-card-handoff">
            {handoffText}
          </span>
        )}
      </span>
      <span style={{ ...gs.chip, ...CHIP[status] }} data-testid="gallery-card-status">{statusText}</span>
    </button>
  );
}

const CHIP = {
  waiting: { background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)' },
  locating: { background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)' },
  signIn: { background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)' },
  viewingAs: { background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)' },
  queued: { background: 'var(--c-info-soft-4)', color: 'var(--c-info-2-fg)' },
  sending: { background: 'var(--c-info-soft-4)', color: 'var(--c-info-2-fg)' },
  saved: { background: 'var(--c-bg-subtle-6)', color: 'var(--c-primary-2-fg)' },
  draft: { background: 'var(--c-bg-subtle-6)', color: 'var(--c-text-muted-3-fg)' },
  attention: { background: 'var(--c-danger-soft-2)', color: 'var(--c-danger-4-fg)' },
  gone: { background: 'var(--c-danger-soft-2)', color: 'var(--c-danger-4-fg)' },
};

const gs = {
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 },
  card: {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 76, padding: 8, textAlign: 'start',
    border: '1px solid var(--c-border-2)', borderRadius: 12, background: 'var(--c-surface)', font: 'inherit',
    color: 'var(--c-text-2-fg)', cursor: 'pointer',
  },
  thumb: {
    flex: '0 0 auto', width: 60, height: 60, borderRadius: 8, overflow: 'hidden', background: 'var(--c-bg-subtle-6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  thumbIcon: { fontSize: 24 },
  handoff: { alignSelf: 'flex-start', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600 },
  text: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  code: { fontSize: 14, fontWeight: 700, textAlign: 'start' },
  meta: { fontSize: 13, color: 'var(--c-text-muted-2-fg)', overflowWrap: 'anywhere' },
  chip: {
    flex: '0 0 auto', padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, maxWidth: 120,
    textAlign: 'center',
  },
};
